/**
 * The label CHIP palette and the tint maths that turns a category colour into a
 * chip's background/foreground pair.
 *
 * It lives in `src/utils/` rather than beside the Settings screen it was written
 * for because five surfaces now draw a label chip — Settings, the Performance
 * data table, the Optimize proposal, the Content tab and the Filters drawer —
 * and the old home (`routes/settings/labels/labelsAdapter.ts`) also carries the
 * label RULE vocabulary, so importing `chipColors` from there pulled
 * `NexoyaContentFilterFieldName`, `NexoyaContentFilterOperator` and the whole
 * clause-describing machinery into every one of those bundles.
 *
 * `labelsAdapter` re-exports both symbols, so the eight call sites that name the
 * palette through it keep working unchanged.
 *
 * Deliberately free of React, Apollo and the generated GraphQL types, so it runs
 * in the `unit` (node) vitest project and `src/components-ui/` may import it —
 * that folder is the one place in the app with a written prohibition on reaching
 * into `routes/`.
 */

/**
 * Category colours, sampled from Figma 12080-19752 / 12080-20123.
 *
 * NINE, not the eight the earlier prototype-derived list carried. The one that
 * was missing is the blue (#1c4fe9); the reason it was easy to miss is that the
 * NINTH swatch is a desaturated grey (#a6a7b5), so a saturation-based count of
 * the row finds only eight chromatic blocks and reads the grey as a divider.
 */
export const LABEL_CATEGORY_COLORS = [
  '#58c9ee',
  '#f89c3f',
  '#8d79f2',
  '#23d77c',
  '#f7da45',
  '#dd55c7',
  '#e95379',
  '#1c4fe9',
  '#a6a7b5',
] as const;

const HEX_COLOR = /^#[0-9a-f]{6}$/;

/**
 * A category colour reduced to the one shape everything downstream assumes:
 * lowercase `#rrggbb`.
 *
 * `color` is a free-text `String!` on the wire: the inputs accept whatever they
 * are sent, and a category created before colours existed carries none (or
 * `''`). Everything downstream — the swatch picker's "selected" comparison
 * against the lowercase palette, and `chipColors`' hex parse — assumes lowercase
 * `#rrggbb`, so that is the only shape let through. Anything else takes the
 * palette's first colour rather than an invisible or unparseable swatch.
 */
export function normalizeCategoryColor(color: string | null | undefined): string {
  const normalized = String(color ?? '')
    .trim()
    .toLowerCase();
  return HEX_COLOR.test(normalized) ? normalized : LABEL_CATEGORY_COLORS[0];
}

/**
 * The colour a NEW category should default to: the first palette entry no existing
 * category uses.
 */
export function nextUnusedCategoryColor(taken: readonly string[] | null | undefined): string {
  const used = new Set((taken ?? []).map(normalizeCategoryColor));
  return LABEL_CATEGORY_COLORS.find((hex) => !used.has(hex)) ?? LABEL_CATEGORY_COLORS[0];
}

// ── the tint maths ──────────────────────────────────────────────────────────

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const cleaned = String(hex ?? '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(cleaned)) return { h: 0, s: 0, l: 50 };

  const r = parseInt(cleaned.substring(0, 2), 16) / 255;
  const g = parseInt(cleaned.substring(2, 4), 16) / 255;
  const b = parseInt(cleaned.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let hue = 0;
  if (delta) {
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  const l = (max + min) / 2;
  const s = delta ? delta / (1 - Math.abs(2 * l - 1)) : 0;
  return { h: Math.round(hue), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** WCAG 2.x relative luminance of an HSL triple, without going via a hex string. */
function hslLuminance(h: number, s: number, l: number): number {
  const sat = s / 100;
  const lig = l / 100;
  const a = sat * Math.min(lig, 1 - lig);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    const v = lig - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(8) + 0.0722 * channel(4);
}

const contrastRatio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/** 13px SemiBold is below the 18.66px large-text threshold, so AA is 4.5:1. */
const CHIP_MIN_CONTRAST = 4.5;

/** Where the frames put the text, and where a hue that already passes stays. */
const CHIP_FG_LIGHTNESS = 30;

/**
 * Label chips are a soft same-hue tint with saturated same-hue text, derived
 * from the category colour so a new category is styled correctly for free.
 * Ported from the prototype's `chipColors`.
 *
 * The LIGHTNESS PAIR alone is not a contrast guarantee. Perceived luminance at a
 * fixed HSL lightness swings hard with hue — green and yellow are far brighter at
 * L=30% than blue or violet — so the prototype's flat 93%/30% left two of the
 * nine palette colours under AA for 13px text (Green 4.32:1, Yellow 4.05:1), and
 * a live teal category measured 3.77:1 on screen.
 *
 * So the foreground is SOLVED rather than assumed: keep the frames' hue, keep
 * their background, and step the text darker only until it clears 4.5:1. Every
 * hue that already passed — Sky, Amber, Violet, Magenta, Rose, Blue, Grey — comes
 * out at exactly the 30% the frames specify, so the palette does not drift; only
 * the two that failed move, and only as far as they must.
 *
 * MEASURED AGAINST FIGMA (pinned in `labelChipColors.test.ts`, not left as prose):
 * FIGMA-SPEC quotes a hand-picked pair per category, and this function agrees
 * with the frames on the background and deliberately disagrees on the text.
 *
 * | category                | frame bg              | derived bg | frame fg           | derived fg |
 * |-------------------------|-----------------------|------------|--------------------|------------|
 * | Country `#20b7e9`       | `#DFF4FB`             | `#dff4fc`  | `#2F91B5` (3.16:1) | 5.24:1     |
 * | Product Family `#744ced`| `rgba(220,213,251,.7)`| `#e6dffc`  | `#6D4FC2` (4.70:1) | 10.51:1    |
 *
 * The backgrounds land within 1 and 3 eight-bit steps respectively — invisible at
 * a chip's size. The Country foreground the frame states is 3.16:1, which 13px
 * text cannot ship at, and the spec itself asks for one derivation rather than
 * two hard-coded cases. So this is drift ON PURPOSE; do not "fix" it back.
 */
export function chipColors(hex: string): { bg: string; fg: string } {
  const { h, s } = hexToHsl(hex);
  const bgSaturation = clamp(s, 25, 85);
  const fgSaturation = clamp(s, 20, 85);

  const backgroundLuminance = hslLuminance(h, bgSaturation, 93);

  // Integer steps, darkest-first stop: deterministic, and it lands on the same
  // value every render without carrying any state. The 6% floor is well clear of
  // where any palette hue needs to go (Yellow, the worst, settles at 26%) and
  // exists only so a pathological hue cannot run to black.
  let lightness = CHIP_FG_LIGHTNESS;
  while (
    lightness > 6 &&
    contrastRatio(hslLuminance(h, fgSaturation, lightness), backgroundLuminance) < CHIP_MIN_CONTRAST
  ) {
    lightness -= 1;
  }

  return {
    bg: `hsl(${h} ${bgSaturation}% 93%)`,
    fg: `hsl(${h} ${fgSaturation}% ${lightness}%)`,
  };
}
